import React, { useState } from 'react'
import { useParams } from 'react-router-dom'
import { SurveyQuestions } from '../../components/survey/response/survey-questions'
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { ArrowRight } from 'lucide-react'
import { SiteHeader } from '../../components/site-header'
import { motion, AnimatePresence } from 'framer-motion'

export default function SurveyRespondPage() {
  const { code } = useParams<{ code: string }>()
  const [showInstructions, setShowInstructions] = useState(true)

  // Check for existing responses in localStorage
  const hasExistingResponses = (() => {
    const savedResponses = localStorage.getItem(`survey_responses_${code}`)
    return savedResponses ? Object.keys(JSON.parse(savedResponses)).length > 0 : false
  })()

  if (!code) {
    return <div>Invalid survey code</div>
  }

  return (
    <div className="flex min-h-screen flex-col">
      <SiteHeader />
      <div className="absolute inset-0 z-0 bg-background">
        <div className="relative w-full h-full">
          <img
            src="/images/backgrounds/flickr-21903280519.jpg"
            alt="Survey background"
            className="object-cover hero-bg w-full h-full"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-background/70 to-background" />
        </div>
      </div>

      <AnimatePresence mode="wait">
        {showInstructions ? (
          <motion.div
            key="instructions"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="container py-24 relative z-10"
          >
            <div className="w-full max-w-3xl mx-auto">
              <Card>
                <CardHeader>
                  <CardTitle>Welcome to the SLK Profile™ Survey</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="space-y-4">
                    <h3 className="font-medium">About This Survey</h3>
                    <p className="text-muted-foreground">
                      This survey will help evaluate your organisation's leadership tendencies and strategic alignment. 
                      The survey consists of four sections and should take about 15-20 minutes to complete.
                    </p>
                    <p className="text-muted-foreground">
                      Your responses will be anonymous and will contribute to a broader understanding of your 
                      organisation's approach to leadership and decision-making.
                    </p>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-medium">Survey Structure</h3>
                    <div className="space-y-2">
                      <div>
                        <h4 className="text-sm font-medium">Section 1: Role Selection</h4>
                        <p className="text-sm text-muted-foreground">Select your role within the organisation.</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium">Section 2: Approach to Leadership</h4>
                        <p className="text-sm text-muted-foreground">Rate how strongly you agree or disagree with statements about your organisation's current approach.</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium">Section 3: Current Focus</h4>
                        <p className="text-sm text-muted-foreground">Indicate where your organisation currently sits on each spectrum.</p>
                      </div>
                      <div>
                        <h4 className="text-sm font-medium">Section 4: Aspirational Focus</h4>
                        <p className="text-sm text-muted-foreground">Consider where your organisation should ideally be positioned to best achieve its goals.</p>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <h3 className="font-medium">Guidelines</h3>
                    <ul className="list-disc pl-6 space-y-2 text-sm text-muted-foreground">
                      <li>Answer based on your organisation's actual tendencies, not what you wish they were</li>
                      <li>Consider the organisation as a whole, not just your team or department</li>
                      <li>If unsure, go with your first instinct</li>
                      <li>There are no "right" or "wrong" answers - be honest in your assessment</li>
                    </ul>
                  </div>

                  <Button 
                    onClick={() => setShowInstructions(false)} 
                    className="w-full"
                  >
                    {hasExistingResponses ? 'Continue Survey' : 'Start Survey'} <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </CardContent>
              </Card>
            </div>
          </motion.div>
        ) : (
          <motion.div
            key="survey"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative z-10 flex-1"
          >
            <SurveyQuestions 
              surveyCode={code} 
              onBackToInstructions={() => setShowInstructions(true)}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}